import React, { useState, useEffect } from 'react';
import usePermissions from 'hooks/permissions-hooks';
import AppLoader from 'components/basic/AppLoader';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { useItems } from 'hooks/atoms/items-hook';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Select, Typography, FormControl, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ManagersDoctorsDetails from 'components/business/local-manager/ManagersDoctorsDetails';
import * as XLSX from 'xlsx';
import axiosClient from 'http/axios-client';
import { roles } from '../cassandra-shared-js/roles';
import { parseDate } from '../components/business/consents/ConsentsTable';

const useStyles = makeStyles({
  title: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 35,
  },
  subtitle: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 18,
  },
  button: {
    margin: '6px 15px 0px 0px',
    fontSize: 18,
    borderColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  dialogPaper: {
    width: '100%',
    maxWidth: 800,
  },
  selectLocation: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '0px 0px 20px 5px',
  },
  searchBox: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '6px 0px 0px 5px',
  },
  tableContainer: {
    margin: 'auto',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    fontWeight: 600,
    fontSize: 16,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  infoButton: {
    color: '#ffffff',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  verifiedButton: {
    color: '#800202',
    borderColor: '#800202',
    fontWeight: 600,
    fontSize: 13,
    '&:hover': {
      backgroundColor: '#800202',
      color: '#ffffff',
    },
  },
  unverifiedButton: {
    color: '#004400',
    borderColor: '#004400',
    fontWeight: 600,
    fontSize: 13,
    '&:hover': {
      backgroundColor: '#004400',
      color: '#ffffff',
    },
  },
  verified: {
    color: '#004400',

    fontWeight: 600,
    fontSize: 15,
  },
  unverified: {
    color: '#800202',
    fontWeight: 600,
    fontSize: 15,
  },
});

export const specialities = new Map([
  [ 'ΟΡΘΟΠΕΔΙΚΗ', 'ORTHOPEDIC' ],
  [ 'ΔΙΑΙΤΟΛΟΓΙΑ - ΔΙΑΤΡΟΦΟΛΟΓΙΑ', 'NUTRITION - DIETETICS' ],
  [ 'ΜΑΙΕΥΤΙΚΗ', 'OBSTETRICS' ],
  [ 'ΕΝΔΟΚΡΙΝΟΛΟΓΙΑ', 'ENDOCRINOLOGY' ],
  [ 'ΝΟΣΗΛΕΥΤΙΚΗ', 'NURSING' ],
  [ 'ΠΛΑΣΤΙΚΗ ΧΕΙΡΟΥΡΓΙΚΗ', 'PLASTIC SURGERY' ],
  [ 'ΠΑΙΔΟΨΥΧΙΑΤΡΙΚΗ', 'CHILD PSYCHIATRY' ],
  [ 'ΑΙΣΘΗΤΙΚΗ', 'AESTHETICS' ],
  [ 'ΠΑΘΟΛΟΓΙΑ', 'PATHOLOGY' ],
  [ 'ΓΥΝΑΙΚΟΛΟΓΙΑ', 'GYNECOLOGY' ],
  [ 'ΦΥΣΙΚΟΘΕΡΑΠΕΙΑ', 'PHYSICAL THERAPY' ],
  [ 'ΠΑΙΔΙΑΤΡΙΚΗ', 'PEDIATRICS' ],
  [ 'ΦΥΣΙΚΗ ΑΓΩΓΗ ΚΑΙ ΑΘΛΗΤΙΣΜΟΣ', 'PHYSICAL EDUCATION AND SPORTS' ],
  [ 'ΨΥΧΙΑΤΡΙΚΗ', 'PSYCHIATRY' ],
  [ 'ΔΕΡΜΑΤΟΛΟΓΙΑ', 'DERMATOLOGY' ],
  [ 'ΚΑΡΔΙΟΛΟΓΙΑ', 'CARDIOLOGY' ],
  [ 'ΔΙΑΤΡΟΦΟΛΟΓΙΑ', 'NUTRITION' ],
  [ 'ΔΙΑΒΗΤΟΛΟΓΙΑ', 'DIABETOLOGY' ],
  [ 'ΝΕΦΡΟΛΟΓΙΑ', 'NEPHROLOGY' ],
  [ 'ΓΕΝΙΚΗ ΙΑΤΡΙΚΗ', 'GENERAL MEDICINE' ],
  [ 'ΝΕΥΡΟΛΟΓΙΑ', 'NEUROLOGY' ],
  [ 'ΦΥΣΙΑΤΡΙΚΗ', 'PHYSICAL MEDICINE' ],
  [ 'ΧΕΙΡΟΥΡΓΙΚΗ', 'SURGERY' ],
]);

function ManagerDoctorsPage() {
  const classes = useStyles();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ country, setCountry ] = useState('GR');
  const { userHasRole, role } = usePermissions();
  const [ searchName, setSearchName ] = useState('');
  const [ openDialog, setOpenDialog ] = useState(false);
  const [ selected, setSelected ] = useState({});
  const [ updateDoctor, setUpdateDoctor ] = useState(false);

  // Select Country
  useEffect(() => {
    if (userHasRole(roles.AL_MANAGER)) {
      setCountry('AL');
    } else if (userHasRole(roles.ADMINISTRATOR_ROLE)) {
      setCountry('GR');
    }
    refetch();
  }, [ role ]);

  // get doctors

  const {
    countFiltered,
    items: doctors,
    itemsLoading: doctorsLoading,
    refetch,
    itemsFetching,
    itemsError: doctorsError,
    pagination,
  } = useItems(CollectionKeys.DOCTORS, { filterQuery: {
    ...filterQuery,
    country,
  } });

  if (doctorsError) {
    return (
      <span>
        Error:
        {doctorsError}
      </span>
    );
  }

  // Download
  const downloadData = () => {
    function fitToColumn(arrayOfArray) {
      if (!arrayOfArray[0]) { // If file is empty
        return null;
      }
      return Object.keys(arrayOfArray[0]).map((key) => {
        return { wch: key.length };
      });
    }

    // Get Data
    axiosClient.get(CollectionKeys.DOCTORS, { params: {
      _limit: -1,
      ...filterQuery,
      country,
    } }).then(async (allDoctors) => {
      if (allDoctors == null || allDoctors.length === 0) {
        return;
      }
      const data = allDoctors.map((doctor) => {
        const obj = {
          id: doctor?.id,
          name: doctor?.name || '',
          surname: doctor?.family_name || '',
          specialty: specialities.get(doctor?.specialty) || '',
          email: doctor?.email || '',
          'Business Phone': doctor?.business_number,
          'Personal Phone': (doctor?.phone_number ? doctor?.phone_number : doctor?.phoneNumber) || '',
          status: doctor?.verification_status || '',
          created: parseDate(doctor?.created_at) || '',
        };
        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet['!cols'] = fitToColumn(data);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, 'Doctors Reports.xlsx');
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = pagination.sort.orderBy === property && pagination.sort.order === 'asc';
    pagination.setSort(property, isAsc ? 'desc' : 'asc');
    pagination.setPage(0);
  };

  const handleSearch = (value) => {
    const newFilter = { family_name_contains: value };
    setFilterQuery({ ...filterQuery, ...newFilter });
    refetch();
  };

  // Start search with Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchName(e.target.value);
      handleSearch(searchName);
    }
  };

  // change doctors status
  const handleUpdateDoctor = async (doctor) => {
    setUpdateDoctor(true);
    if (doctor.verification_status === 'VERIFIED') {
      await axiosClient.put(`doctors/${ doctor.id }`, { verification_status: 'UNVERIFIED' });
    }
    if (
      doctor.verification_status === 'UNVERIFIED'
      || doctor.verification_status === ''
      || doctor.verification_status === undefined
      || doctor.verification_status === null) {
      await axiosClient.put(`doctors/${ doctor.id }`, { verification_status: 'VERIFIED' });
    }
    refetch();
    setUpdateDoctor(false);
  };

  return (
    <Grid container spacing={ 2 } alignItems='center'>
      <Grid item xs={ 12 }>
        <div className={ classes.title }>
          Doctors Management
        </div>
        <div className={ classes.subtitle }>
          Entries:
          {' '}
          {countFiltered}
        </div>
      </Grid>
      {userHasRole(roles.ADMINISTRATOR_ROLE)
      && (
        <Grid item xs={ 6 } md={ 2 } display='flex'>
          <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
            <Typography
              variant='subtitle'
              color='initial'
              id='location-select-label'
            >
              Location:
            </Typography>
            <Select
              variant='outlined'
              size='small'
              labelId='country-select-label'
              id='country-select'
              value={ country }
              onChange={ (e) => { setCountry(e.target.value); refetch(); } }
            >
              <MenuItem value='GR'>Greece</MenuItem>
              <MenuItem value='AL'>Albania</MenuItem>
              <MenuItem value='IN'>India</MenuItem>
              <MenuItem value='IQ'>Iraq</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={ 6 } md={ 2 } display='flex'>
        <TextField
          className={ classes.searchBox }
          id='search-bar'
          size='medium'
          value={ searchName }
          onChange={ (e) => { setSearchName(e.target.value); } }
          onKeyPress={ (e) => { handleKeyPress(e); } }
          label='Search Doctors Surname'
          variant='outlined'
          defaultValue='20px Text'
        />
      </Grid>
      <Grid item xs={ 6 } md={ 6 } display='flex'>
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => { handleSearch(searchName); } }
        >
          <SearchIcon />
          SEARCH
        </Button>
        { searchName.length > 0
      && (
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => {
            setSearchName('');
            setFilterQuery({});
          } }
        >
          CLEAR
        </Button>
      )}
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ downloadData }
        >
          Download
        </Button>
      </Grid>
      <Grid item xs={ 12 } md={ 12 }>
        <Paper>
          <TableContainer className={ classes.tableContainer }>
            {(doctorsLoading || itemsFetching || updateDoctor) ? <AppLoader />
              : (
                <Table className={ classes.table }>
                  <TableHead>
                    <TableRow>
                      <TableCell className={ classes.headerCell }>Name</TableCell>
                      <TableCell className={ classes.headerCell }>Surname</TableCell>
                      <TableCell className={ classes.headerCell }>Specialty</TableCell>
                      <TableCell
                        className={ classes.headerCell }
                        sortDirection={ pagination.sort.orderBy === 'created_at' ? pagination.sort.order : false }
                      >
                        <TableSortLabel
                          active={ pagination.sort.orderBy === 'created_at' }
                          direction={ pagination.sort.orderBy === 'created_at' ? pagination.sort.order : 'asc' }
                          onClick={ () => { return handleRequestSort('created_at'); } }
                        >
                          Created At
                        </TableSortLabel>

                      </TableCell>
                      <TableCell
                        className={ classes.headerCell }
                        sortDirection={ pagination.sort.orderBy === 'verification_status' ? pagination.sort.order : false }
                      >
                        <TableSortLabel
                          active={ pagination.sort.orderBy === 'verification_status' }
                          direction={ pagination.sort.orderBy === 'verification_status' ? pagination.sort.order : 'asc' }
                          onClick={ () => { return handleRequestSort('verification_status'); } }
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={ classes.headerCell }>Actions</TableCell>
                      <TableCell className={ classes.headerCell }>More</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {doctors.map((doctor) => {
                      return (
                        <TableRow key={ doctor.id } className={ classes.row }>
                          <TableCell>{doctor?.name}</TableCell>
                          <TableCell>{doctor?.family_name}</TableCell>
                          <TableCell>{specialities.get(doctor?.specialty) || ''}</TableCell>
                          <TableCell>{parseDate(doctor?.created_at)}</TableCell>
                          <TableCell className={ doctor?.verification_status === 'VERIFIED'
                            ? classes.verified : classes.unverified }
                          >
                            {doctor?.verification_status}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant='outlined'
                              className={ doctor.verification_status === 'VERIFIED'
                                ? classes.verifiedButton : classes.unverifiedButton }
                              onClick={ () => { handleUpdateDoctor(doctor); } }
                            >
                              { doctor.verification_status === 'VERIFIED'
                                ? 'DISCLAIM DOCTOR' : 'VERIFY DOCTOR'}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              className={ classes.infoButton }
                              onClick={ () => {
                                setSelected(doctor);
                                setOpenDialog(true);
                              } }
                            >
                              More
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </TableContainer>
          <TablePagination
            component='div'
            count={ countFiltered }
            page={ pagination.page }
            onChangePage={ (e, p) => {
              pagination.setPage(p);
            } }
            rowsPerPage={ pagination.limit }
            onChangeRowsPerPage={
              (e) => {
                const newLimit = parseInt(e.target.value, 10);
                pagination.setLimit(newLimit);
                pagination.setPage(0);
              }
            }
            rowsPerPageOptions={ [ 5, 10, 25 ] }
          />
        </Paper>
        <Dialog
          classes={ { paper: classes.dialogPaper } }
          open={ openDialog }
          onClose={ () => { setSelected({}); setOpenDialog(false); } }
        >
          <DialogContent style={ { backgroundColor: '#f3f3f3' } }>
            <DialogContentText>
              <ManagersDoctorsDetails id={ selected?.id } />
            </DialogContentText>

          </DialogContent>
          <DialogActions>
            <Button
              className={ classes.button }
              onClick={ () => { setOpenDialog(false); } }
              autoFocus
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}

export default ManagerDoctorsPage;
